/**
 * 1.3.0 the Delivery panel (P2): a placeholder's assignment, each requested item's state, and Edit / Cancel.
 * Shown from the asset page's "Delivery" button (AssignForDelivery.tsx); exported for an inline mount too.
 */
import { useState } from 'react';
import { Ban, CheckCircle2, CircleDashed, FileUp, Loader2, Pencil, XCircle } from 'lucide-react';
import type { AssignmentState, ItemState } from '@prism/contracts';
import { errorMessage } from '../../api/client';
import { useMe } from '../../api/hooks';
import { useCancelAssignment, type AssignmentDTO } from '../../api/hooks/assignments';
import { toast } from '../../components/Toasts';
import { AssignDialog } from './AssignDialog';
import { formatDue } from './zoned';

export const ASSIGNMENT_STATE_LABEL: Record<AssignmentState, string> = {
  open: 'Open', in_progress: 'In progress', in_review: 'In review', accepted: 'Accepted', cancelled: 'Cancelled', expired: 'Expired',
};
const STATE_PILL: Record<AssignmentState, string> = { open: 'accent', in_progress: 'accent', in_review: 'warning', accepted: 'ready', cancelled: '', expired: 'danger' };
const ITEM_LABEL: Record<ItemState, string> = { open: 'Waiting', uploaded: 'Uploaded', accepted: 'Accepted', rejected: 'Rejected', waived: 'Waived' };
const ITEM_ICON: Record<ItemState, typeof CircleDashed> = { open: CircleDashed, uploaded: FileUp, accepted: CheckCircle2, rejected: XCircle, waived: Ban };
const fmtBytes = (n: number) => (n >= 1024 ** 3 ? `${(n / 1024 ** 3).toFixed(1)} GiB` : n >= 1024 ** 2 ? `${(n / 1024 ** 2).toFixed(1)} MiB` : `${Math.max(1, Math.round(n / 1024))} KiB`);

export function AssignmentStatePill({ a }: { a: Pick<AssignmentDTO, 'state' | 'overdue' | 'outcome'> }) {
  return (
    <>
      <span className={`pill ${STATE_PILL[a.state]}`} data-testid="delivery-state">{ASSIGNMENT_STATE_LABEL[a.state]}{a.state === 'accepted' && a.outcome === 'missing_items' ? ', items missing' : ''}</span>
      {a.overdue && <span className="pill danger" data-testid="delivery-overdue">Overdue</span>}
    </>
  );
}

/** `onEdit`: the caller opens the edit dialog (e.g. instead of a dialog over its own); otherwise the panel does. */
export function DeliveryPanel({ assignment: a, onClosed, onEdit }: { assignment: AssignmentDTO; onClosed?: () => void; onEdit?: () => void }) {
  const me = useMe().data;
  const canAssign = Boolean(me?.permissions.includes('portal.assign'));
  const cancel = useCancelAssignment();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const editable = a.state === 'open' || a.state === 'in_progress';
  const cancellable = a.state === 'open' || a.state === 'in_progress' || a.state === 'in_review';

  async function doCancel() {
    try {
      await cancel.mutateAsync({ id: a.id, reason });
      toast(`Delivery of ${a.asset.assetCode} cancelled; the supplier is told`);
      setConfirming(false);
      onClosed?.();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  return (
    <section className="delivery-panel" data-testid="delivery-panel">
      <header className="delivery-head">
        <h3>Delivery</h3>
        <AssignmentStatePill a={a} />
        <span className="delivery-spacer" />
        {canAssign && editable && <button className="btn sm" onClick={() => (onEdit ? onEdit() : setEditing(true))} data-testid="delivery-edit"><Pencil size={13} /> Edit</button>}
        {canAssign && cancellable && !confirming && <button className="btn ghost sm" onClick={() => setConfirming(true)} data-testid="delivery-cancel"><Ban size={13} /> Cancel delivery</button>}
      </header>
      {confirming && (
        <div className="delivery-confirm" data-testid="delivery-cancel-confirm">
          <span>Cancel this delivery? {a.portalUser ? a.portalUser.name : `The users of ${a.company.name}`} will be emailed and can no longer upload into it.</span>
          <input className="input" placeholder="Reason (kept in the activity log)" value={reason} maxLength={2000} onChange={(e) => setReason(e.target.value)} data-testid="delivery-cancel-reason" />
          <div className="delivery-confirm-actions">
            <button className="btn ghost sm" onClick={() => setConfirming(false)}>Keep it</button>
            <button className="btn danger sm" disabled={cancel.isPending} onClick={() => void doCancel()} data-testid="delivery-cancel-confirm-button">
              {cancel.isPending && <Loader2 size={13} className="spin" />} Cancel delivery
            </button>
          </div>
        </div>
      )}
      <dl className="delivery-facts">
        <dt>Deliver by</dt><dd data-testid="delivery-who">{a.portalUser ? `${a.portalUser.name}, ${a.company.name}` : `${a.company.name} (every user)`}</dd>
        <dt>Due</dt><dd data-testid="delivery-due" className={a.overdue ? 'danger-text' : ''}>{formatDue(a.dueAt)}</dd>
        <dt>Spec</dt><dd>{a.spec?.name ?? 'None'}</dd>
        <dt>Contact</dt><dd>{a.contact?.name ?? '—'}</dd>
        {a.notes && <><dt>Notes</dt><dd className="delivery-notes">{a.notes}</dd></>}
      </dl>
      <ul className="delivery-items">
        {a.items.map((i) => {
          const Icon = ITEM_ICON[i.state];
          return (
            <li key={i.id} className={`delivery-item state-${i.state}`} data-testid="delivery-item" data-state={i.state}>
              <Icon size={15} className="delivery-item-icon" />
              <div className="delivery-item-body">
                <div className="delivery-item-title">
                  <strong>{i.label}</strong>
                  {!i.required && <span className="faint">optional</span>}
                  <span className="delivery-spacer" />
                  <span className="delivery-item-state">{ITEM_LABEL[i.state]}</span>
                </div>
                {i.description && <span className="muted">{i.description}</span>}
                {i.file && <span className="faint">{i.file.filename} · {fmtBytes(i.file.sizeBytes)}</span>}
                {i.state === 'rejected' && i.reason && <span className="danger-text">Rejected: {i.reason}</span>}
              </div>
            </li>
          );
        })}
      </ul>
      <p className="faint delivery-foot">Assigned {new Date(a.createdAt).toLocaleDateString()}. The supplier uploads through the media portal; uploads wait in Intake.</p>
      {editing && <AssignDialog existing={a} onClose={() => setEditing(false)} />}
    </section>
  );
}
